/**
 * QZ Tray Printer Service
 * Handles printing via QZ Tray for USB/Network/Bluetooth printers
 */

import qz from 'qz-tray'
import {
  buildQRISCommands,
  buildReceiptCommands,
  buildWebOrderCommands,
  buildShiftDetailCommands,
  buildLabelingCommands,
} from './escposBuilder'
import { buildReceiptCommands as buildPlainReceiptCommands } from './plainBuilder'
import { buildLabelingCommands as buildZPLLabelingCommands } from './zplLabelBuilder'
import { buildLabelingCommands as buildTSPLLabelingCommands } from './tsplLabelBuilder'
import { setupQZCertificate } from './qzCertificate'
import { isOptionEnabled } from '../helper'
import { getTableReceiptLabel } from './receiptTableHelper'

// Setup certificate on module load
let certificateSetup = false

/**
 * Check if QZ Tray is available
 */
export const isQZTrayAvailable = () => {
  return typeof qz !== 'undefined' && qz.websocket
}

/**
 * Connect to QZ Tray
 */
export const connectQZTray = async () => {
  try {
    if (!isQZTrayAvailable()) {
      throw new Error('QZ Tray tidak tersedia. Pastikan QZ Tray sudah terinstall dan berjalan.')
    }

    // Setup certificate (only once)
    if (!certificateSetup) {
      setupQZCertificate()
      certificateSetup = true
    }

    // Check if already connected
    if (qz.websocket.isActive()) {
      return { success: true, message: 'QZ Tray sudah terhubung' }
    }

    // Try to connect with retry
    await qz.websocket.connect({
      retries: 3,
      delay: 1,
    })

    console.log('✅ QZ Tray connected successfully')
    return { success: true, message: 'QZ Tray berhasil terhubung' }
  } catch (error) {
    console.error('❌ QZ Tray connection failed:', error)
    throw new Error(`Gagal terhubung ke QZ Tray: ${error.message}`)
  }
}

/**
 * Disconnect from QZ Tray
 */
export const disconnectQZTray = async () => {
  try {
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect()
      console.log('QZ Tray disconnected')
    }
  } catch (error) {
    console.error('Error disconnecting QZ Tray:', error)
  }
}

/**
 * Get list of available printers
 */
export const getQZPrinters = async () => {
  try {
    // Ensure connected
    await connectQZTray()

    // Get all printers
    const printers = await qz.printers.find()

    console.log('Available printers:', printers)
    return printers
  } catch (error) {
    console.error('Error getting printers:', error)
    throw error
  }
}

/**
 * Find thermal printer
 * Searches for common thermal printer names
 */
export const findThermalPrinter = async () => {
  try {
    const printers = await getQZPrinters()

    // Common thermal printer keywords
    const thermalKeywords = [
      'thermal',
      'pos',
      'epson',
      'tm-',
      'receipt',
      'rp',
      'xp',
      'star',
      'tsp',
      'zebra',
      'zp',
    ]

    // Find first printer that matches keywords
    const thermalPrinter = printers.find((printer) => {
      const printerLower = printer.toLowerCase()
      return thermalKeywords.some((keyword) => printerLower.includes(keyword))
    })

    if (thermalPrinter) {
      console.log('✅ Thermal printer found:', thermalPrinter)
      return thermalPrinter
    }

    // If no thermal printer found, return first printer
    if (printers.length > 0) {
      console.log('⚠️ No thermal printer found, using first printer:', printers[0])
      return printers[0]
    }

    throw new Error('Tidak ada printer yang tersedia')
  } catch (error) {
    console.error('Error finding thermal printer:', error)
    throw error
  }
}

/**
 * Get default printer
 */
export const getDefaultPrinter = async () => {
  try {
    await connectQZTray()
    const defaultPrinter = await qz.printers.getDefault()
    console.log('Default printer:', defaultPrinter)
    return defaultPrinter
  } catch (error) {
    console.error('Error getting default printer:', error)
    throw error
  }
}

/**
 * Check if printer is online/ready (simplified version)
 * Just checks if printer exists in available printers list
 * @param {string} printerName - Nama printer yang ingin dicek
 * @returns {Promise<{online: boolean, ready: boolean, message: string}>}
 */
export const isPrinterReady = async (printerName) => {
  try {
    await connectQZTray()

    const availablePrinters = await getQZPrinters()
    const printerExists = availablePrinters.some(
      (printer) => printer?.toLowerCase() === printerName?.toLowerCase(),
    )

    return printerExists
  } catch (error) {
    console.error('Error checking if printer is ready:', error)
    return false
  }
}

/**
 * Get builder function based on print command
 * @param {string} printCommand - Print command name (e.g., 'plain', 'ESC/POS', 'ZPL', 'TSPL')
 * @returns {Function} Builder function
 */
const getBuilderByCommand = (printCommand) => {
  const commandName = (printCommand || '').toLowerCase().trim()

  switch (commandName) {
    case 'plain':
      return buildPlainReceiptCommands
    case 'esc/pos':
    case 'escpos':
      return buildReceiptCommands
    default:
      // Default to ESC/POS for backward compatibility
      return buildReceiptCommands
  }
}

/**
 * Build receipt commands based on print command type
 * @param {Object} receiptData - Receipt data
 * @param {Object} printOptions - Print options including print_command
 * @param {Function} formatMessage - i18n formatMessage function
 * @returns {Promise<Array>|Array} Commands array
 */
export const buildReceiptCommandsByCommand = async (
  receiptData,
  printOptions = {},
  formatMessage,
) => {
  const { print_command } = printOptions
  const commandName = (print_command || '').toLowerCase().trim()

  // Handle ZPL labeling
  if (commandName === 'zpl' && printOptions.printerFunction === 'labeling') {
    return buildZPLLabelingCommands(receiptData, printOptions, formatMessage)
  }

  // Handle TSPL labeling
  if (commandName === 'tspl' && printOptions.printerFunction === 'labeling') {
    return buildTSPLLabelingCommands(receiptData, printOptions, formatMessage)
  }

  // Handle ESC/POS labeling separately
  // For labeling, we need to use buildLabelingCommands from escposBuilder
  // which handles individual label data, not receipt data
  if (printOptions.printerFunction === 'labeling') {
    // Return null to indicate that labeling should be handled separately
    // The caller should handle labeling with buildLabelingCommands
    return null
  }

  const builder = getBuilderByCommand(print_command)
  if (!builder) {
    // Fallback to ESC/POS if builder not found
    return buildReceiptCommands(receiptData, printOptions, formatMessage)
  }

  // Build commands using selected builder
  return builder(receiptData, printOptions, formatMessage)
}

/**
 * Print receipt via QZ Tray
 */
export const printViaQZTray = async (receiptData, printOptions = {}, formatMessage) => {
  try {
    // Ensure connected
    await connectQZTray()

    // Get printer name or network config
    const { printerName, print_connection, print_ip, print_port } = printOptions

    const printCommandName = (printOptions.print_command || '').toLowerCase().trim()

    // For raw printing (ESC/POS, ZPL, TSPL), network config is OK
    let printerConfig = printerName
    if (print_connection === 'network' && print_ip && print_port) {
      printerConfig = {
        host: print_ip,
        port: parseInt(print_port, 10),
      }
    }

    if (!printerConfig) {
      if (print_connection === 'network' && (!print_ip || !print_port)) {
        throw new Error('IP Address dan Port harus diisi untuk koneksi Network')
      }
      throw new Error('Printer name atau konfigurasi network harus diisi')
    }

    // Create printer config
    // Untuk ESC/POS raw, hindari UTF-8 karena byte multi-byte (>=0x80)
    // bisa tampil sebagai karakter acak di printer (mis. "Ç").
    const isTsplLabeling =
      printOptions.printerFunction === 'labeling' &&
      (printCommandName === 'tspl' || printCommandName === 'epl')

    let configOptions = {
      ...printOptions.config,
    }

    // Special config for TSPL/EPL labeling - use ISO-8859-1 encoding
    if (isTsplLabeling) {
      configOptions = {
        ...configOptions,
        encoding: 'ISO-8859-1', // Use ISO-8859-1 encoding for TSPL/EPL printers
      }
    }

    // Special config for ESC/POS QR code - use ISO-8859-1 encoding to ensure binary commands are sent correctly
    // This prevents binary escape sequences from being interpreted as text (e.g., "1P0" issue)
    if (printOptions.qris || printOptions.webOrder) {
      configOptions = {
        ...configOptions,
        encoding: 'ISO-8859-1', // Use ISO-8859-1 encoding for ESC/POS QR code commands
      }
    }

    const config = qz.configs.create(printerConfig, configOptions)

    // Build commands based on print_command
    let commands = []

    // Handle labeling separately (ZPL, TSPL, or ESC/POS) - must be checked before print_command
    if (printOptions.printerFunction === 'labeling') {
      if (printCommandName === 'zpl') {
        // ZPL labeling (raw commands)
        // ZPL commands are returned as array of strings (one per label)
        // Each label's commands are already split into array of strings
        const zplCommandsArray = buildZPLLabelingCommands(receiptData, printOptions, formatMessage)

        // Execute ZPL label prints sequentially
        // For ZPL, send array of strings directly (QZ Tray recognizes ZPL format)
        let zplChain = Promise.resolve()
        zplCommandsArray.forEach((zplLabelCommands) => {
          zplChain = zplChain.then(() => qz.print(config, zplLabelCommands))
        })
        await zplChain

        return {
          success: true,
          message: 'Label ZPL berhasil dicetak',
          method: 'qz-tray',
          printer:
            print_connection === 'network' && print_ip && print_port
              ? `${print_ip}:${print_port}`
              : printerName,
        }
      }

      if (printCommandName === 'tspl' || printCommandName === 'epl') {
        // TSPL/EPL labeling (raw commands)
        // Note: EPL is similar to TSPL, so we use TSPL builder
        // TSPL commands are returned as array of arrays (one array per label)
        const tsplCommandsArray = buildTSPLLabelingCommands(
          receiptData,
          printOptions,
          formatMessage,
        )

        // Execute TSPL label prints sequentially
        // For TSPL, send array of strings directly (QZ Tray recognizes TSPL format)
        let tsplChain = Promise.resolve()
        tsplCommandsArray.forEach((tsplLabelCommands) => {
          tsplChain = tsplChain.then(() => qz.print(config, tsplLabelCommands))
        })
        await tsplChain

        return {
          success: true,
          message: 'Label TSPL berhasil dicetak',
          method: 'qz-tray',
          printer:
            print_connection === 'network' && print_ip && print_port
              ? `${print_ip}:${print_port}`
              : printerName,
        }
      }

      // ESC/POS labeling (default or when print_command is ESC/POS)
      const items = receiptData?.items || []
      const labelPrintTasks = []

      items.forEach((item) => {
        const qty = Math.abs(item.qty || 1)
        const sequences = Array.from({ length: qty }, (_, idx) => idx + 1)
        sequences.forEach((sequence) => {
          const labelData = {
            qty,
            productName: item.product?.name || '-',
            modifiers: item.modifiers || [],
            sequence,
            contactName: isOptionEnabled(printOptions.outletOptions?.print_style_show_customer)
              ? receiptData.contact?.name
              : undefined,
            tableLabel:
              printOptions.outletOptions?.print_style_show_order_type === '1'
                ? getTableReceiptLabel(receiptData)
                : undefined,
          }

          labelPrintTasks.push(() => {
            const labelCommands = buildLabelingCommands(labelData, {
              paperWidth: printOptions.paperWidth,
              enableCut: printOptions.enableCut,
            })

            return qz.print(config, labelCommands)
          })
        })
      })

      // Execute label prints sequentially
      let labelChain = Promise.resolve()
      labelPrintTasks.forEach((task) => {
        labelChain = labelChain.then(() => task())
      })
      await labelChain

      return {
        success: true,
        message: 'Label berhasil dicetak',
        method: 'qz-tray',
        printer:
          print_connection === 'network' && print_ip && print_port
            ? `${print_ip}:${print_port}`
            : printerName,
      }
    }

    if (printOptions.qris) {
      // Legacy: Build ESC/POS commands (backward compatibility)
      commands = buildQRISCommands(
        receiptData,
        {
          printerFunction: printOptions.printerFunction || 'struk',
          outlet: printOptions.outlet,
          companyOptions: printOptions.companyOptions,
          outletOptions: printOptions.outletOptions,
          paperWidth: printOptions.paperWidth, // '80mm', '58mm', or number,
          enableCut: printOptions.enableCut,
          enableOpenCashDrawer: printOptions.enableOpenCashDrawer,
          qrModuleSize: printOptions.qrModuleSize || 6,
        },
        formatMessage,
      )
    } else if (printOptions.webOrder) {
      commands = buildWebOrderCommands(
        receiptData,
        {
          printerFunction: printOptions.printerFunction || 'struk',
          outlet: printOptions.outlet,
          companyOptions: printOptions.companyOptions,
          outletOptions: printOptions.outletOptions,
          paperWidth: printOptions.paperWidth, // '80mm', '58mm', or number,
          enableCut: printOptions.enableCut,
          enableOpenCashDrawer: printOptions.enableOpenCashDrawer,
          qrModuleSize: printOptions.qrModuleSize || 6,
        },
        formatMessage,
      )
    } else if (printOptions.shiftDetail) {
      commands = buildShiftDetailCommands(
        receiptData,
        {
          outlet: printOptions.outlet,
          companyOptions: printOptions.companyOptions,
          outletOptions: printOptions.outletOptions,
          paperWidth: printOptions.paperWidth, // '80mm', '58mm', or number,
          enableCut: printOptions.enableCut,
          withProduct: printOptions.withProduct || false,
          withPaymentDetail:
            printOptions.withPaymentDetail !== undefined ? printOptions.withPaymentDetail : true,
        },
        formatMessage,
      )
    } else {
      // Hormati print_command (plain / ESC-POS). 'plain' → plainBuilder (tanpa logo raster),
      // ESC/POS → buildReceiptCommands yang sama seperti sebelumnya (output identik).
      commands = await buildReceiptCommandsByCommand(receiptData, printOptions, formatMessage)
    }

    // Print - QZ Tray expects array format for ESC/POS commands
    await qz.print(config, commands)

    return {
      success: true,
      message: 'Struk berhasil dicetak',
      method: 'qz-tray',
      printer:
        print_connection === 'network' && print_ip && print_port
          ? `${print_ip}:${print_port}`
          : printerName,
    }
  } catch (error) {
    console.error('❌ QZ Tray print failed:', error)
    throw new Error(`Gagal mencetak via QZ Tray: ${error.message}`)
  }
}

/**
 * Chaining print requests (sequential) untuk beberapa printer.
 * Referensi konsep chaining: https://qz.io/docs/raw#escpos
 *
 * @param {Array<{
 *   printerName: string,
 *   receiptData: any,
 *   printOptions?: object,
 *   config?: object
 * }>} jobs
 * @param {{ continueOnError?: boolean }} options
 * @returns {Promise<{success: boolean, results: Array, errors: Array}>}
 */
export const printChainedViaQZTray = async (jobs = [], options = {}, formatMessage) => {
  const { continueOnError = false } = options

  if (!Array.isArray(jobs) || jobs.length === 0) {
    return { success: true, results: [], errors: [] }
  }

  // Ensure connected once
  await connectQZTray()

  const results = []
  const errors = []

  // Jalankan berurutan (bukan paralel) untuk menghindari race / buffer overflow
  let chain = Promise.resolve()
  jobs.forEach((job) => {
    chain = chain.then(async () => {
      const printerName = job?.printerName
      const printOptions = job?.printOptions || {}
      const { print_connection, print_ip, print_port } = printOptions

      const printCommandName = (printOptions.print_command || '').toLowerCase().trim()
      const isTsplLabeling =
        printOptions.printerFunction === 'labeling' &&
        (printCommandName === 'tspl' || printCommandName === 'epl')

      // For raw printing (ESC/POS, ZPL, TSPL), network config is OK
      let printerConfig = printerName
      if (print_connection === 'network' && print_ip && print_port) {
        printerConfig = {
          host: print_ip,
          port: parseInt(print_port, 10),
        }
      }

      if (!printerConfig) {
        let err
        if (print_connection === 'network' && (!print_ip || !print_port)) {
          err = new Error('IP Address dan Port harus diisi untuk koneksi Network')
        } else {
          err = new Error('Printer name atau konfigurasi network harus diisi')
        }
        errors.push({ job, error: err })
        if (!continueOnError) {
          throw err
        }
        return
      }

      try {
        // Create printer config
        let configOptions = {
          ...(job?.config || {}),
        }

        // Special config for TSPL/EPL labeling - use ISO-8859-1 encoding
        if (isTsplLabeling || printOptions.qris || printOptions.webOrder) {
          configOptions = {
            ...configOptions,
            encoding: 'ISO-8859-1', // Use ISO-8859-1 encoding for TSPL/EPL printers
          }
        }

        const config = qz.configs.create(printerConfig, configOptions)

        let commands = []

        // Handle labeling differently - need to print multiple labels per item
        if (printOptions.printerFunction === 'labeling') {
          const receiptData = job?.receiptData

          if (printCommandName === 'zpl') {
            // ZPL labeling (raw commands)
            // ZPL commands are returned as array of arrays (one array per label)
            const zplCommandsArray = buildZPLLabelingCommands(
              receiptData,
              printOptions,
              formatMessage,
            )

            // Execute ZPL label prints sequentially
            // For ZPL, send array of strings directly (QZ Tray recognizes ZPL format)
            let zplChain = Promise.resolve()
            zplCommandsArray.forEach((zplLabelCommands) => {
              zplChain = zplChain.then(() => qz.print(config, zplLabelCommands))
            })
            await zplChain
          } else if (printCommandName === 'tspl' || printCommandName === 'epl') {
            // TSPL/EPL labeling (raw commands)
            // TSPL commands are returned as array of arrays (one array per label)
            const tsplCommandsArray = buildTSPLLabelingCommands(
              receiptData,
              printOptions,
              formatMessage,
            )

            // Execute TSPL label prints sequentially
            // For TSPL, send array of strings directly (QZ Tray recognizes TSPL format)
            let tsplChain = Promise.resolve()
            tsplCommandsArray.forEach((tsplLabelCommands) => {
              tsplChain = tsplChain.then(() => qz.print(config, tsplLabelCommands))
            })
            await tsplChain
          } else {
            // ESC/POS labeling
            const items = receiptData?.items || []

            // For labeling, we need to print each item multiple times based on qty
            // Each print is a separate label
            // Create array of label print tasks
            const labelPrintTasks = []
            items.forEach((item) => {
              const qty = Math.abs(item.qty || 1)
              const sequences = Array.from({ length: qty }, (_, idx) => idx + 1)
              sequences.forEach((sequence) => {
                const labelData = {
                  qty,
                  productName: item.product?.name || '-',
                  modifiers: item.modifiers || [],
                  sequence,
                  contactName: isOptionEnabled(
                    printOptions.outletOptions?.print_style_show_customer,
                  )
                    ? receiptData.contact?.name
                    : undefined,
                  tableLabel:
                    printOptions.outletOptions?.print_style_show_order_type === '1'
                      ? getTableReceiptLabel(receiptData)
                      : undefined,
                }

                labelPrintTasks.push(() => {
                  const labelCommands = buildLabelingCommands(labelData, {
                    paperWidth: printOptions.paperWidth,
                    enableCut: printOptions.enableCut,
                  })

                  return qz.print(config, labelCommands)
                })
              })
            })

            // Execute label prints sequentially
            let labelChain = Promise.resolve()
            labelPrintTasks.forEach((task) => {
              labelChain = labelChain.then(() => task())
            })
            await labelChain
          }
        } else {
          // For other print functions, use existing logic
          if (printOptions.qris) {
            commands = buildQRISCommands(
              job?.receiptData,
              {
                printerFunction: printOptions.printerFunction || 'struk',
                outlet: printOptions.outlet,
                companyOptions: printOptions.companyOptions,
                outletOptions: printOptions.outletOptions,
                paperWidth: printOptions.paperWidth,
                enableCut: printOptions.enableCut,
                enableOpenCashDrawer: printOptions.enableOpenCashDrawer,
                qrModuleSize: printOptions.qrModuleSize || 6,
              },
              formatMessage,
            )
          } else if (printOptions.webOrder) {
            commands = buildWebOrderCommands(
              job?.receiptData,
              {
                printerFunction: printOptions.printerFunction || 'struk',
                outlet: printOptions.outlet,
                companyOptions: printOptions.companyOptions,
                outletOptions: printOptions.outletOptions,
                paperWidth: printOptions.paperWidth,
                enableCut: printOptions.enableCut,
                enableOpenCashDrawer: printOptions.enableOpenCashDrawer,
                qrModuleSize: printOptions.qrModuleSize || 6,
              },
              formatMessage,
            )
          } else if (printOptions.shiftDetail) {
            commands = buildShiftDetailCommands(
              job?.receiptData,
              {
                outlet: printOptions.outlet,
                companyOptions: printOptions.companyOptions,
                outletOptions: printOptions.outletOptions,
                paperWidth: printOptions.paperWidth,
                enableCut: printOptions.enableCut,
                withProduct: printOptions.withProduct || false,
                withPaymentDetail:
                  printOptions.withPaymentDetail !== undefined
                    ? printOptions.withPaymentDetail
                    : true,
              },
              formatMessage,
            )
          } else {
            // Hormati print_command (plain / ESC-POS). Untuk 'plain' → plainBuilder (tanpa logo raster),
            // untuk ESC/POS → buildReceiptCommands yang sama seperti sebelumnya (output identik).
            commands = await buildReceiptCommandsByCommand(
              job?.receiptData,
              printOptions,
              formatMessage,
            )
          }

          await qz.print(config, commands)
        }

        results.push({
          printer: printerName,
          printerFunction: printOptions.printerFunction,
          success: true,
        })
      } catch (error) {
        console.log('error', error)
        errors.push({
          printer: printerName,
          printerFunction: printOptions.printerFunction,
          success: false,
          error,
        })
        if (!continueOnError) {
          throw error
        }
      }
    })
  })

  await chain
  return { success: errors.length === 0, results, errors }
}

/**
 * Test print - simple test receipt
 */
export const testPrintQZTray = async (printerName, options = {}) => {
  try {
    await connectQZTray()

    const config = qz.configs.create(printerName || (await findThermalPrinter()))

    const { enableOpenCashDrawer = false, enableCut = true } = options

    const testData = [
      '\x1B\x40', // Initialize
    ]

    // Open cash drawer if enabled
    if (enableOpenCashDrawer) {
      testData.push('\x1B\x70\x00\x19\xFA') // Open cash drawer
    }

    testData.push(
      '\x1B\x61\x01', // Center align
      'TEST PRINT\x0A',
      '\x0A', // line break
      '\x1B\x61\x30', // Left align
      'QZ Tray is working!\x0A',
      `Date: ${new Date().toLocaleString()}\x0A`,
      '\x0A', // line break
      '================================\x0A',
      '\x0A', // line break
      '\x0A', // line break
      '\x0A', // line break
    )

    // Paper cut if enabled
    if (enableCut) {
      testData.push('\x1D\x56\x00') // Full cut (new syntax sesuai dokumentasi QZ Tray)
    }

    await qz.print(config, testData)

    return {
      success: true,
      message: 'Test print berhasil',
    }
  } catch (error) {
    console.error('Test print failed:', error)
    throw error
  }
}

/**
 * Test print with receipt data - uses builder based on print_command
 * @param {Object} receiptData - Receipt data untuk test print
 * @param {Object} printOptions - Print options including print_command, printerName, etc.
 * @param {Function} formatMessage - i18n formatMessage function
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const testPrintReceipt = async (receiptData, printOptions = {}, formatMessage) => {
  try {
    await connectQZTray()

    // Get printer name or network config
    const { printerName, print_connection, print_ip, print_port } = printOptions

    const printCommandName = (printOptions.print_command || '').toLowerCase().trim()
    const isTsplLabeling =
      printOptions.printerFunction === 'labeling' &&
      (printCommandName === 'tspl' || printCommandName === 'epl')

    // For raw printing (ESC/POS, ZPL, TSPL), network config is OK
    let printerConfig = printerName
    if (print_connection === 'network' && print_ip && print_port) {
      printerConfig = {
        host: print_ip,
        port: parseInt(print_port, 10),
      }
    }

    if (!printerConfig) {
      if (print_connection === 'network' && (!print_ip || !print_port)) {
        throw new Error('IP Address dan Port harus diisi untuk koneksi Network')
      }
      throw new Error('Printer name atau konfigurasi network harus diisi')
    }

    // Create printer config
    let configOptions = {
      ...printOptions.config,
    }

    // Special config for TSPL/EPL labeling - use ISO-8859-1 encoding
    if (isTsplLabeling) {
      configOptions = {
        ...configOptions,
        encoding: 'ISO-8859-1', // Use ISO-8859-1 encoding for TSPL/EPL printers
      }
    }

    const config = qz.configs.create(printerConfig, configOptions)

    // Build commands based on print_command

    // Handle labeling separately (ZPL, TSPL, or ESC/POS)
    if (printOptions.printerFunction === 'labeling') {
      if (printCommandName === 'zpl') {
        // ZPL labeling (raw commands)
        // ZPL commands are returned as array of arrays (one array per label)
        const zplCommandsArray = buildZPLLabelingCommands(receiptData, printOptions, formatMessage)

        // Execute ZPL label prints sequentially
        // For ZPL, send array of strings directly (QZ Tray recognizes ZPL format)
        let zplChain = Promise.resolve()
        zplCommandsArray.forEach((zplLabelCommands) => {
          zplChain = zplChain.then(() => qz.print(config, zplLabelCommands))
        })
        await zplChain
      } else if (printCommandName === 'tspl' || printCommandName === 'epl') {
        // TSPL/EPL labeling (raw commands)
        // TSPL commands are returned as array of arrays (one array per label)
        const tsplCommandsArray = buildTSPLLabelingCommands(
          receiptData,
          printOptions,
          formatMessage,
        )

        // Execute TSPL label prints sequentially
        // For TSPL, send array of strings directly (QZ Tray recognizes TSPL format)
        let tsplChain = Promise.resolve()
        tsplCommandsArray.forEach((tsplLabelCommands) => {
          tsplChain = tsplChain.then(() => qz.print(config, tsplLabelCommands))
        })
        await tsplChain
      } else {
        // ESC/POS labeling (default or when print_command is ESC/POS)
        const items = receiptData?.items || []
        const labelPrintTasks = []

        items.forEach((item) => {
          const qty = Math.abs(item.qty || 1)
          const sequences = Array.from({ length: qty }, (_, idx) => idx + 1)
          sequences.forEach((sequence) => {
            const labelData = {
              qty,
              productName: item.product?.name || '-',
              modifiers: item.modifiers || [],
              sequence,
              contactName: isOptionEnabled(printOptions.outletOptions?.print_style_show_customer)
                ? receiptData.contact?.name
                : undefined,
              tableLabel:
                printOptions.outletOptions?.print_style_show_order_type === '1'
                  ? getTableReceiptLabel(receiptData)
                  : undefined,
            }

            labelPrintTasks.push(() => {
              const labelCommands = buildLabelingCommands(labelData, {
                paperWidth: printOptions.paperWidth,
                enableCut: printOptions.enableCut,
              })

              return qz.print(config, labelCommands)
            })
          })
        })

        // Execute label prints sequentially
        let labelChain = Promise.resolve()
        labelPrintTasks.forEach((task) => {
          labelChain = labelChain.then(() => task())
        })
        await labelChain
      }
    } else {
      // Non-labeling: use command-based builder
      const commands = await buildReceiptCommandsByCommand(receiptData, printOptions, formatMessage)

      // For plain command, use direct array format (no wrapper object)
      // QZ Tray plain flavor works best with direct array of strings
      if (printCommandName === 'plain') {
        // Plain flavor: send array directly, QZ Tray will handle it as plain text
        // No need to wrap in object, just use the array as-is
      }

      // Print commands
      await qz.print(config, commands)
    }

    return {
      success: true,
      message: 'Test print berhasil',
    }
  } catch (error) {
    console.error('Test print failed:', error)
    throw error
  }
}

/**
 * Get QZ Tray version
 */
export const getQZVersion = async () => {
  try {
    await connectQZTray()
    const version = await qz.api.getVersion()
    console.log('QZ Tray version:', version)
    return version
  } catch (error) {
    console.error('Error getting QZ version:', error)
    return null
  }
}

/**
 * Check if QZ Tray desktop application is installed and running
 * Returns true if QZ Tray application is available (installed and running)
 * This checks if the QZ Tray desktop app is running, not the npm library
 * We try to connect to verify the desktop app is actually running
 * @returns {Promise<boolean>}
 */
export const checkQZTrayInstalled = async () => {
  try {
    // Check if qz object exists
    if (typeof qz === 'undefined') {
      return false
    }

    // Check if qz.websocket exists
    if (typeof qz.websocket === 'undefined' || qz.websocket === null) {
      return false
    }

    // Check if already connected
    if (qz.websocket.isActive && qz.websocket.isActive()) {
      return true
    }

    // Try to connect with very short timeout to verify desktop app is running
    // If desktop app is not running, this will fail immediately
    try {
      await Promise.race([
        qz.websocket.connect({ retries: 0, delay: 0 }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Connection timeout')), 1500)),
      ])

      // Connection succeeded - desktop app is running
      return true
    } catch (error) {
      // Connection failed - check error message to determine if desktop app is not running
      const errorMessage = error?.message || String(error) || ''

      // Common errors when desktop app is not running:
      // - "Connection refused", "ECONNREFUSED", "WebSocket connection failed"
      // - "Connection timeout" means desktop app might not be responding
      console.log('QZ Tray desktop app check failed:', errorMessage)
      return false
    }
  } catch (error) {
    console.error('Error checking QZ Tray installation:', error)
    return false
  }
}

/**
 * Check QZ Tray status dengan batasan waktu (timeout)
 * Jika QZ Tray tidak merespons dalam waktu yang ditentukan, langsung dianggap tidak terhubung
 * untuk menghindari stuck loading saat QZ Tray belum dijalankan.
 * @param {number} timeoutMs - Batas waktu pengecekan dalam milidetik (default: 8000 = 8 detik)
 * @returns {Promise<{available: boolean, connected: boolean, version?: string, message: string}>}
 */
export const checkQZStatus = async (timeoutMs = 8000) => {
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('QZ_STATUS_TIMEOUT')), timeoutMs),
  )

  const checkPromise = (async () => {
    try {
      if (!isQZTrayAvailable()) {
        return {
          available: false,
          connected: false,
          message: 'QZ Tray tidak tersedia',
        }
      }

      // Selalu lakukan verifikasi fresh - disconnect dulu jika sudah connected
      // untuk hindari state koneksi usang saat user kembali ke halaman Printer
      if (qz.websocket.isActive()) {
        try {
          await qz.websocket.disconnect()
        } catch {
          // Ignore disconnect error
        }
      }

      // Try to connect - gunakan retry minimal agar cepat gagal jika QZ Tray tidak berjalan
      try {
        if (!certificateSetup) {
          setupQZCertificate()
          certificateSetup = true
        }
        await qz.websocket.connect({ retries: 1, delay: 0 })
        const version = await qz.api.getVersion()

        return {
          available: true,
          connected: true,
          version,
          message: 'QZ Tray siap digunakan',
        }
      } catch (error) {
        return {
          available: true,
          connected: false,
          message: 'QZ Tray tersedia tapi tidak terhubung',
        }
      }
    } catch (error) {
      return {
        available: false,
        connected: false,
        message: error?.message || 'QZ Tray tidak tersedia',
      }
    }
  })()

  try {
    return await Promise.race([checkPromise, timeoutPromise])
  } catch (error) {
    // Timeout - QZ Tray tidak merespons dalam waktu yang ditentukan
    if (error?.message === 'QZ_STATUS_TIMEOUT') {
      return {
        available: true,
        connected: false,
        message: 'QZ Tray belum dijalankan. Pastikan QZ Tray sudah running.',
      }
    }
    return {
      available: false,
      connected: false,
      message: error?.message || 'QZ Tray tidak tersedia',
    }
  }
}

export default {
  isAvailable: isQZTrayAvailable,
  connect: connectQZTray,
  disconnect: disconnectQZTray,
  getPrinters: getQZPrinters,
  findThermalPrinter,
  getDefaultPrinter,
  isPrinterReady,
  print: printViaQZTray,
  printChained: printChainedViaQZTray,
  testPrint: testPrintQZTray,
  testPrintReceipt,
  buildReceiptCommandsByCommand,
  getVersion: getQZVersion,
  checkStatus: checkQZStatus,
  checkInstalled: checkQZTrayInstalled,
}
